import { datadogRum } from '@datadog/browser-rum';
import { ConfigProvider } from 'antd';
import { FC, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { map } from 'rxjs';
import { getPublicIp, sessionId } from 'src/utils/common';
import { datadogConfig, isLocal, version } from 'src/utils/config';
import { observeProductGroupForCategorySubset, observePromotionSection } from 'src/utils/firebase/firebase-short-cut';
import { logger } from 'src/utils/logger';

import useHomeConf from './redux/hooks/useHomeConf';

import useAuthentication from './hooks/useAuthentication';

import { AppProvider } from './stores/app-context';

import './App.scss';
import Router from './Router';

// 알려진 봇 인스턴스와 일치하는 regex 패턴:
const botPattern = "(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)";
const regex = new RegExp(botPattern, 'i');

//  userAgent가 botPatterns와 패턴이 일치하면 conditionalSampleRate를 0으로 설정
// 그 외에는 conditionalSampleRate를 100으로 정의
const conditionalSampleRate = regex.test(navigator.userAgent) ? 0 : 100;

// RUM Browser SDK를 초기 실행하고 sessionSampleRate를 conditionalSampleRate로 설정

if (!isLocal && datadogConfig.applicationId && datadogConfig.clientToken) {
  datadogRum.init({
    applicationId: datadogConfig.applicationId,
    clientToken: datadogConfig.clientToken,
    site: datadogConfig.site,
    service: datadogConfig.service,
    env: datadogConfig.env,
    version,
    /**
     * 일반 세션의 수집 비율
     * 예) 100명의 사용자 중 50명의 수집을 원한다면 50으로 설정해보자.
    */
    sessionSampleRate: conditionalSampleRate,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    /**
     * Replay recordings에서의 민감정보 설정
     *
     * 'mask-user-input': 리플레이에서 form안에 들어가는 input 텍스트를 마스킹 처리한다.
     * 'mask': 리플레이에서 화면의 모든 텍스트를 마스킹 처리한다.
     * 'allow': 모든 내용을 캡쳐한다.
     */
    defaultPrivacyLevel: 'allow',
    // allowedTracingUrls: [
    //   'https://api.gooduncles.com',
    //   /https:\/\/.*\.gooduncles\.com/,
    // ],
  });
  datadogRum.startSessionReplayRecording();
}

getPublicIp().then((ip) => {
  logger.setPublicIp(ip);
});
logger.logCommerce(`세션 시작: ${sessionId()}`);

const App: FC = () => {
  const { onSetProductGroupForCategories, onSetPromotionSections } = useHomeConf();
  // 로그인 관련 로직 처리
  useAuthentication();

  // HomeConf
  useEffect(() => {
    const subscription = observeProductGroupForCategorySubset().subscribe(onSetProductGroupForCategories);

    const promotionSubscription = observePromotionSection([['isDeleted', '!=', true]])
      .pipe(map((docs) => docs?.filter((doc) => doc.isActive).sort((a, b) => a.positionIndex - b.positionIndex)))
      .subscribe(onSetPromotionSections);
    return () => {
      subscription.unsubscribe();
      promotionSubscription.unsubscribe();
    };
  }, [onSetProductGroupForCategories, onSetPromotionSections]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#FF7F4C',
        },
      }}>
      <BrowserRouter>
        <AppProvider>
          <Router />
        </AppProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default App;
